/* Recently viewed.
   1. On a product page, records the handle (last 8) in localStorage "ldy-recent". The handle comes from
      <meta name="ldy-product-handle">, the section's data-current, or ShopifyAnalytics (pageType "product") + URL.
   2. <ldy-recently-viewed> stays hidden until history exists; near the viewport it fetches up to 8 cards in
      parallel from /products/{handle}?section_id=ldy-card-single (cached 10 min per session) into a rail. */
(() => {
  if (customElements.get('ldy-recently-viewed')) return;
  const KEY = 'ldy-recent';
  const CACHE = 'ldy-recent-cards';
  const MAX = 8;
  const TTL = 10 * 60 * 1000;

  const read = (store, key, fallback) => {
    try {
      return JSON.parse(store.getItem(key)) || fallback;
    } catch {
      return fallback;
    }
  };
  const write = (store, key, value) => {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch {
      /* storage blocked or full: history is skipped */
    }
  };

  function currentHandle() {
    const fromPage =
      document.querySelector('meta[name="ldy-product-handle"]')?.content ||
      document.querySelector('ldy-recently-viewed[data-current]')?.dataset.current;
    if (fromPage) return fromPage;
    if (window.ShopifyAnalytics?.meta?.page?.pageType !== 'product') return '';
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  const current = currentHandle();
  if (current)
    write(localStorage, KEY, [current, ...read(localStorage, KEY, []).filter((h) => h !== current)].slice(0, MAX));

  async function fetchCard(handle, cache, currency, gone) {
    const hit = cache[handle];
    if (hit && hit.t > Date.now() - TTL && hit.c === currency) return hit.html;
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=ldy-card-single`);
    if (res.status === 404) {
      gone.push(handle);
      return '';
    }
    if (!res.ok) throw new Error(`Card request failed (${res.status})`);
    const card = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('ldy-card');
    const html = card ? card.outerHTML : '';
    cache[handle] = { t: Date.now(), c: currency, html };
    return html;
  }

  class LdyRecentlyViewed extends HTMLElement {
    connectedCallback() {
      const limit = Math.min(Number(this.dataset.limit) || MAX, MAX);
      const skip = this.dataset.current || current;
      const handles = read(localStorage, KEY, [])
        .filter((h) => h && h !== skip)
        .slice(0, limit);
      if (!handles.length || this.started) return;
      this.started = true;
      if (!('IntersectionObserver' in window)) {
        this.render(handles);
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          this.render(handles);
        },
        { rootMargin: '600px 0px' },
      );
      io.observe(this);
    }

    async render(handles) {
      const cache = read(sessionStorage, CACHE, {});
      const currency = window.Shopify?.currency?.active || '';
      const gone = [];
      const results = await Promise.allSettled(handles.map((h) => fetchCard(h, cache, currency, gone)));
      results.forEach((r) => r.status === 'rejected' && console.error('[ldy-recently-viewed]', r.reason));
      if (gone.length)
        write(
          localStorage,
          KEY,
          read(localStorage, KEY, []).filter((h) => !gone.includes(h)),
        );
      write(sessionStorage, CACHE, cache);

      /* Same-origin section HTML rendered by Shopify (not user input). */
      const html = results.map((r) => (r.status === 'fulfilled' ? r.value : '')).join('');
      if (!html) return;
      this.querySelector('[data-list]').innerHTML = html;
      this.querySelector('[data-wrap]').hidden = false;
    }
  }

  customElements.define('ldy-recently-viewed', LdyRecentlyViewed);
})();
